/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 封装原生方法, based on sentry
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */

import { isFunction, isUndefined } from './is';

/**
 * 封装方法
 */
interface WrapFunction extends Function {
    /** 是否封装过 */
    __raven__: boolean;

    /** 封装的原始方法 */
    __orig__: Function;

    /** 封装 */
    __raven_wrapper__: Function;

    [k: string]: any;
}

/**
 * 将 func 使用 try/catch 封装，如果有异常，抛出异常，并返回新封装的函数
 * @param options 参数
 * @param func 待封装函数
 * @param _before 待封装函数执行前执行的函数
 */
export function wrap(options: { [k: string]: any } = {}, func: WrapFunction | any, _before?: Function) {

    // 1 argument has been passed, and it's not a function
    // so just return it
    if (isUndefined(func) && !isFunction(options)) {
        return options;
    }

    // 选项可选
    if (isFunction(options)) {
        func = options;
        options = undefined;
    }

    // At this point, we've passed along 2 arguments, and the second one
    // is not a function either, so we'll just return the second argument.
    if (!isFunction(func)) {
        return func;
    }

    try {
        // 避免重复封装
        if (func.__raven__) {
            return func;
        }

        // 如果已经封装过，返回封装过的函数
        if (func.__raven_wrapper__) {
            return func.__raven_wrapper__;
        }
    } catch (e) {
        // Just accessing custom props in some Selenium environments
        // can cause a "Permission denied" exception (see raven-js#495).
        // Bail on wrapping and return the function as-is (defers to window.onerror).
        return func;
    }

    function wrapped(...args: any[]) {
        const newArgs: any[] = [];
        let len = args.length;
        const deep = !options || (options && options.deep !== false);
        if (_before && isFunction(_before)) {
            _before.apply(this, args);
        }

        while (len--) {
            // 如果 deep 为 true，递归封装底层的函数
            newArgs[len] = deep ? wrap(options, args[len]) : args[len];
        }

        try {
            // 调用封装函数，如果看到此处的堆栈，意味着通过监控平台捕获到代码异常，这是预期行为，而不是BUG
            return func.apply(this, newArgs);
        } catch (e) {
            throw e;
        }
    }

    // 原来使用 Object.keys() 会存在部分报错的问题，替换成 for/in 循环先
    for (const prop in func) {
        if (func.hasOwnProperty(prop)) {
            wrapped[prop] = func[prop];
        }
    }
    wrapped.prototype = func.prototype;

    func.__raven_wrapper__ = wrapped;
    wrapped.__raven__ = true;
    wrapped.__orig__ = func;

    return wrapped;
}

/**
 * 使用 try...catch 封装定时器的回调函数
 * @param orig 原生定时器函数，例如 window.setTimeout, window.setInterval 等
 * @return Function
 */
export function wrapTimerFn(orig: Function) {

    return function (...args: any[]) {
        const originalCallback = args[0];
        if (isFunction(originalCallback)) {
            args[0] = wrap(
                {
                    mechanism: {
                        type: 'instrument',
                        data: { function: orig?.name || '<anonymous>' }
                    }
                },
                originalCallback
            );
        }

        if (orig.apply) {
            return orig.apply(this, args);
        }
        return orig(args[0], args[1]);
    };
}
